"use client"

import { useState, useMemo, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useLocation } from "react-router-dom"
import { login, signup, clearError, logout, REMEMBERED_EMAIL_KEY } from "../../store/slices/authSlice"
import { clearSendOtp } from "../../store/slices/sendOtpSlice"
import { clearRegisterPasswordState } from "../../store/slices/registerPasswordSlice"
import { fetchUserProfileAfterLogin, clearUserProfile } from "../../store/slices/userSlice"
import { fetchClientsByUserId, clearClients } from "../../store/slices/clientsSlice"
import {
  markPropertySignupRequired,
  isMissingOrganizationError,
} from "../../lib/utils/propertySignupRedirect"
import { getAuthMessage } from "../../lib/constants/authStatusCodes"
import "./LoginPage.css"
import { FaEye, FaEyeSlash } from "react-icons/fa"
import { sendResetLink, clearResetState } from "../../store/slices/resetPasswordSlice"
import PasswordEyeIcon from "../../lib/utils/PasswordEyeIcon"
import ResetLinkSuccessPopup from "../common/ResetLinkSuccessPopup/ResetLinkSuccessPopup"
import ForgotPasswordPopup from "../common/ForgotPasswordPopup/ForgotPasswordPopup"


const readDynamic = (key, fallback) => {
  try {
    if (typeof window !== "undefined") {
      if (window[key] && typeof window[key] === "string") return window[key]
      const stored = localStorage.getItem(key)
      if (stored) return stored
    }
  } catch { }
  return fallback
}

const isValidEmail = (value) => {
  if (!value || typeof value !== "string") return false
  const trimmed = value.trim()
  if (!trimmed) return false
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(trimmed)
}

const LoginPage = () => {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [rememberMe, setRememberMe] = useState(false)
  const [isSignUp, setIsSignUp] = useState(false)
  const [infoMessage, setInfoMessage] = useState("")
  const [showForgotPopup, setShowForgotPopup] = useState(false)
  const [forgotEmail, setForgotEmail] = useState("")
  const [showResetSuccessPopup, setShowResetSuccessPopup] = useState(false)
  const [resetLinkHeading, setResetLinkHeading] = useState("Password reset link sent")
  const resetError = useSelector((state) => state.resetPassword.error)
  const [resetEmail, setResetEmail] = useState("")
  const [forgotLocalError, setForgotLocalError] = useState("")
  const [emailError, setEmailError] = useState("")


  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const authStatus = useSelector((state) => state?.auth?.status)
  const authError = useSelector((state) => state?.auth?.error)
  const signUpStatus = useSelector((state) => state?.auth?.signUpStatus)
  const signUpError = useSelector((state) => state?.auth?.signUpError)

  const brandName = useMemo(() => readDynamic("APP_BRAND_NAME", "SoHO"), [])
  const welcomeText = useMemo(() => readDynamic("APP_WELCOME_TEXT", "Welcome to"), [])
  const bgUrl = useMemo(() => readDynamic("LOGIN_BG_URL", "assets/images/login-bg.jpg"), [])
  const [showPassword, setShowPassword] = useState(false)

  useEffect(() => {
    try {
      const remembered = typeof window !== "undefined" && localStorage.getItem(REMEMBERED_EMAIL_KEY)
      if (remembered && remembered.trim()) {
        setEmail(remembered.trim())
        setRememberMe(true)
      }
    } catch { }
  }, [])


  const handleSubmit = async (e) => {
    e.preventDefault()
    setInfoMessage("")
    setEmailError("")

    if (!isValidEmail(email)) {
      setEmailError("Please enter a valid email address.")
      return
    }

    try {
      if (isSignUp) {
        await dispatch(
          signup({
            username: email.trim(),
            email: email.trim(),
            password: password,
          }),
        ).unwrap()
        setEmail("")
        setPassword("")
        setIsSignUp(false)
        setInfoMessage("Account created. Please sign in.")
      } else {
        dispatch(clearUserProfile())
        dispatch(clearClients())
        dispatch(clearError())

        await dispatch(login({ username: email, password, rememberMe })).unwrap()

        // try {
        //   const userProfile = await dispatch(fetchUserProfileAfterLogin()).unwrap()

        //   if (userProfile?.id) {
        //     try {
        //       await dispatch(fetchClientsByUserId(userProfile.id)).unwrap()
        //     } catch (clientsErr) {
        //     }
        //   }
        // } catch (userErr) {
        // }

        // const returnUrl = location.state?.from?.pathname || "/calendar"
        // navigate(returnUrl, { replace: true })
        try {
          const userProfile = await dispatch(fetchUserProfileAfterLogin()).unwrap()

          if (userProfile?.id) {
            try {
              const clientsResponse = await dispatch(
                fetchClientsByUserId(userProfile.id),
              ).unwrap()

              const clientList = Array.isArray(clientsResponse)
                ? clientsResponse
                : Array.isArray(clientsResponse?.data)
                  ? clientsResponse.data
                  : Array.isArray(clientsResponse?.clients)
                    ? clientsResponse.clients
                    : []

              if (clientList.length === 0) {
                markPropertySignupRequired()
                navigate("/signup", { replace: true })
                return
              }
            } catch (clientsErr) {
              if (isMissingOrganizationError(clientsErr)) {
                markPropertySignupRequired()
                navigate("/signup", { replace: true })
                return
              }
              dispatch(logout())
              dispatch(clearUserProfile())
              dispatch(clearClients())
              setInfoMessage("Unable to load your properties. Please try again.")
              return
            }
          }
        } catch {
          dispatch(logout())
          dispatch(clearUserProfile())
          dispatch(clearClients())
          setInfoMessage("Unable to load your account. Please try again.")
          return
        }

        navigate("/calendar", { replace: true })
      }
    } catch (err) {
      // No console.error or alert needed - just let the error display naturally
    }
  }

  const currentStatus = isSignUp ? signUpStatus : authStatus
  const currentError = isSignUp ? signUpError : authError
  const isLoading = currentStatus === "loading"

  return (
    <div
      className="login-container"
      style={{
        backgroundImage: "url(/assets/illustrations/soho-image.png)",
        backgroundRepeat: "no-repeat",
        backgroundSize: "150% auto",
        backgroundPosition: "top 25px right -100px",
      }}
    >
      <div className="login-background">
        <img src={bgUrl || "/placeholder.svg"} alt="Background" className="background-image" />
      </div>

      <div className="div-nXy9B">
        <div className="div-2">
          <div className="div-3">
            <div className="div-4">
              <div className="div-4-brand"><img src="/assets/brand/rategain_logo.png" /></div>
              <div className="div-5">
                <strong>{isSignUp ? "Create account" : welcomeText}</strong>
                <img src="/assets/brand/soho_social.svg" alt="Brand Logo" className="login-logo-container" />
              </div>
            </div>
          </div>
          {/* <div className="div-6">{isSignUp ? "Create account" : welcomeText}</div> */}
        </div>

        <form onSubmit={handleSubmit} className="div-7">
          <div className="div-8">
            <div className="div-9 mt-40">
              <div className="div-10">
                <label className="div-11" htmlFor="email-input">
                  Email address
                </label>
              </div>
              <div className="div-12">
                <div className="div-13">
                  <input
                    className="div-14"
                    id="email-input"
                    placeholder="Enter your email"
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value)
                      if (emailError) setEmailError("")
                    }}
                    required
                  />
                  {emailError && <p className="login-error-below-password" style={{ marginTop: "4px" }}>{emailError}</p>}
                  <div className="div-15">
                    <div className="div-16">
                      <div className="div-17" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="div-18 mt-40">
              <div className="div-19">
                <div className="div-20">Password</div>
              </div>
              <div className="div-21">
                <div className="div-22 position-relative">
                  <input
                    className="div-14"
                    type={showPassword ? "text" : "password"}
                    placeholder={isSignUp ? "Create a password" : "Enter your password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <span className="eye-icon" onClick={() => setShowPassword(!showPassword)}>
                    <PasswordEyeIcon isVisible={showPassword} />
                  </span>
                  <div className="div-24">
                    <div className="div-25">
                      <div className="div-26" />
                    </div>
                  </div>
                </div>
              </div>
              {currentError && <p className="login-error-below-password">{getAuthMessage(currentError)}</p>}
            </div>

            {/* {!isSignUp && (
              <div className="div-27">
                <div className="div-28">
                  <input
                    className="div-29"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  <div className="div-30">
                    <div className="div-31">Remember me</div>
                  </div>
                </div>
                <div className="div-32" />
              </div>
            )} */}
            <div className="rem-forg">
              <div className="rememberMe">
                <input
                  type="checkbox"
                  id="remember-me"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <label htmlFor="remember-me">Remember me</label>
              </div>
              <div className="forgotPass" onClick={() => setShowForgotPopup(true)}>Forgot password?</div>
            </div>


            <button type="submit" className="div-33" disabled={isLoading}>
              <div className="div-34">
                {isLoading ? (isSignUp ? "Registering..." : "Signing in...") : isSignUp ? "Register" : "Sign in"}
              </div>
            </button>
            <div className="createAccount">New to SoHo? <span onClick={() => {
              dispatch(clearSendOtp())
              dispatch(clearRegisterPasswordState())
              if (!localStorage.getItem("signup_session")) { localStorage.setItem("signup_session", "true") }
              sessionStorage.setItem("signup_current_step", "1")
              sessionStorage.setItem("signup_current_page", "signup")
              sessionStorage.removeItem("signup_step_one_data")
              sessionStorage.removeItem("signup_step_two_data")
              navigate("/signup")
            }}>Create an account</span>
            </div>
          </div>
        </form>

        {infoMessage ? (
          <p className="p login-info-text">{infoMessage}</p>
        ) : (
          <div className="p">
            {isSignUp ? (
              <>
                {"Already have an account? "}
                <button
                  type="button"
                  onClick={() => {
                    setIsSignUp(false)
                    setInfoMessage("")
                  }}
                  className="login-signin-button"
                >
                  Sign in
                </button>
              </>
            ) : null}
          </div>
        )}
      </div>

      <ForgotPasswordPopup
        isOpen={showForgotPopup}
        onClose={() => {
          setShowForgotPopup(false)
          setForgotEmail("")
          setForgotLocalError("")
        }}
        onSuccess={({ heading, email }) => {
          setResetLinkHeading(heading)
          setResetEmail(email)
          setShowForgotPopup(false)
          setShowResetSuccessPopup(true)
        }}
        isValidEmail={isValidEmail}
      />

      <ResetLinkSuccessPopup
        isVisible={showResetSuccessPopup}
        onClose={() => setShowResetSuccessPopup(false)}
        heading={resetLinkHeading}
        email={resetEmail}
        autoCloseSeconds={5}
      />
    </div>
  )
}

export default LoginPage
